
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AcademicCapIcon, SparklesIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { getMyInternships } from '../../api/userApi'; 
import { useNavigate } from 'react-router-dom';

const MyInternships = () => {
    const navigate = useNavigate();
    const [internships, setInternships] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchInterns = async () => {
            try {
                const response = await getMyInternships();
                const actualData = response.success ? response.data : [];
                setInternships(actualData);
            } catch (err) {
                console.error("Fetch Error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchInterns();
    }, []);

    if (loading) return (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center font-mono">
            <div className="text-center italic uppercase font-black text-emerald-500 animate-pulse tracking-widest text-xs">
                FETCHING MISSION DATA...
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#050505] text-white p-6 lg:px-10 py-6">
            <header className="mb-8"> {/* Reduced margin from mb-12 */}
                <h1 className="text-3xl md:text-4xl font-black tracking-tighter italic uppercase"> {/* Shrunk from text-6xl */}
                    Professional <span className="text-emerald-500">Tracks</span>
                </h1>
                <p className="text-gray-500 text-[9px] font-black uppercase tracking-[0.3em] mt-1">
                    Verified Industrial Experiences
                </p>
            </header>

            {/* Change gap-4 to gap-3 for tighter spacing */}
            <div className="grid grid-cols-1 gap-3">
                {internships.length > 0 ? (
                    internships.map((intern) => (
                        <motion.div 
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            key={intern.id}
                            /* Reduced padding from p-8 to p-5 and rounded corners to 1.5rem */
                            className="bg-[#0a0a0a] border border-white/5 p-5 rounded-2xl flex flex-col md:flex-row items-center justify-between hover:bg-white/[0.03] transition-all group relative overflow-hidden"
                        >
                            <div className="flex items-center gap-4"> {/* Reduced gap from 6 to 4 */}
                                {/* Reduced Icon Container size */}
                                <div className="h-12 w-12 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center group-hover:border-emerald-500/50 transition-colors shrink-0">
                                    <AcademicCapIcon className="h-6 w-6 text-emerald-500" />
                                </div>
                                
                                <div>
                                    {/* Shrunk Title from text-2xl to text-lg */}
                                    <h3 className="text-lg font-black italic uppercase leading-tight group-hover:text-emerald-500 transition-colors">
                                        {intern.title}
                                    </h3>
                                    <div className="flex flex-wrap gap-3 items-center mt-1">
                                        <span className="text-[8px] font-black uppercase text-emerald-500 tracking-widest px-1.5 py-0.5 bg-emerald-500/10 rounded border border-emerald-500/20">
                                            {intern.status}
                                        </span>
                                        <div className="flex items-center gap-1 text-gray-500 text-[9px] font-bold uppercase tracking-widest">
                                            <CalendarIcon className="h-3 w-3" />
                                            {intern.duration_months} Month(s)
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Slimmer Button */}
                            <button 
                                onClick={() => navigate(`/dashboard/internship-room/${intern.id}`)}
                                className="mt-4 md:mt-0 px-6 py-2.5 bg-white text-black text-[9px] font-black uppercase tracking-widest hover:bg-emerald-500 hover:text-white transition-all rounded-lg italic shadow-lg active:scale-95 whitespace-nowrap"
                            >
                                Enter Workspace
                            </button>
                        </motion.div>
                    ))
                ) : (
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-center py-16 border border-dashed border-white/10 rounded-[2rem]"
                    >
                        <SparklesIcon className="h-10 w-10 text-gray-800 mx-auto mb-3" />
                        <p className="text-gray-600 text-[10px] font-black uppercase italic tracking-widest">
                            No active tracks found
                        </p>
                    </motion.div>
                )}
            </div>
        </div>
    );
};

export default MyInternships;